import { BaseApiServiceImpl } from "./BaseApiServiceImpl";

export type TransferMethod = "mobile_money" | "bank";

export interface TransferCounterparty {
  msisdn?: string;
  network?: string;
  account_name?: string;
  bank_code?: string;
  account_number?: string;
}

export interface TransferVerificationRequest {
  method: TransferMethod;
  amount: number;
  currency: string;
  counterparty: TransferCounterparty;
  narration?: string;
}

export interface TransferVerificationResponse {
  verificationCode: string;
  expiresAt: string;
  amount: number;
  fee: number;
  net: number;
  currency: string;
  walletAvailable: number;
  sufficient: boolean;
}

export interface TransferWireRequest {
  verificationCode: string;
  idempotencyKey: string;
  method: TransferMethod;
  amount: number;
  currency: string;
  counterparty: TransferCounterparty;
  reference?: string;
  narration?: string;
  /** Fee from the verification response — used to debit amount + fee from the wallet. */
  fee?: number;
}

export interface Transfer {
  /** Local Apollos record id — use for status polling. */
  transactionId?: number | null;
  id: string | null;
  method: string;
  amount: number;
  currency: string;
  fee: number;
  net: number;
  state: string;
  riskDecision?: string;
  failureReason?: string | null;
  verifiedName?: string | null;
  reference?: string | null;
  msisdn?: string | null;
  bankCode?: string | null;
  accountNumber?: string | null;
  createdAt?: string | null;
}

export interface BankDirectoryEntry {
  code: string;
  name: string;
}

export interface TransferStats {
  method: string;
  totalTransactions: number;
  succeeded: number;
  failed: number;
  rejected: number;
  cancelled: number;
  pending: number;
  totalAmount: number;
  totalFee: number;
  totalNet: number;
  succeededAmount: number;
  failedAmount: number;
}

const TERMINAL_STATES = ["succeeded", "failed", "rejected", "cancelled"];

/**
 * KaCyber money transfers: send money from the wallet to MTN/Airtel mobile
 * money or to bank accounts. Two-step flow: request a verification code,
 * then wire the transfer with that code and a fresh idempotencyKey (UUID).
 */
export class MoneyTransferService {
  private static baseUrl = "/api/v1/transfers";

  static isTerminalState(state?: string | null): boolean {
    return !!state && TERMINAL_STATES.includes(state.toLowerCase());
  }

  static async getDirectory(
    type: "BANK" | "TELCO" = "BANK",
    country: string = "UG"
  ): Promise<BankDirectoryEntry[]> {
    const response: any = await new BaseApiServiceImpl(`${this.baseUrl}/banks`).getRequestWithJsonResponse({
      type,
      country,
    });
    const data = response?.data ?? response;
    return Array.isArray(data) ? data : [];
  }

  static async requestVerification(
    request: TransferVerificationRequest
  ): Promise<TransferVerificationResponse> {
    const response: any = await new BaseApiServiceImpl(`${this.baseUrl}/verification`)
      .postRequestWithJsonResponse(request);
    return response?.data ?? response;
  }

  static async wireTransfer(request: TransferWireRequest): Promise<Transfer> {
    const response: any = await new BaseApiServiceImpl(this.baseUrl).postRequestWithJsonResponse(request);
    return response?.data ?? response;
  }

  static async getTransfer(id: number): Promise<Transfer> {
    const response: any = await new BaseApiServiceImpl(`${this.baseUrl}/${id}`).getRequestWithJsonResponse({});
    return response?.data ?? response;
  }

  static async listTransfers(params: {
    offset: number;
    limit: number;
    searchTerm?: string;
    status?: string;
  }): Promise<{ records: any[]; totalRecords: number }> {
    const queryParameters: any = { offset: params.offset, limit: params.limit };
    if (params.searchTerm) queryParameters.searchTerm = params.searchTerm;
    if (params.status) queryParameters.status = params.status;
    const response: any = await new BaseApiServiceImpl(`${this.baseUrl}/ui`).getRequestWithJsonResponse(
      queryParameters
    );
    return { records: response?.records || [], totalRecords: response?.totalItems || 0 };
  }

  static async getTransferStats(method?: string): Promise<TransferStats> {
    const queryParameters: any = {};
    if (method) queryParameters.method = method;
    const response: any = await new BaseApiServiceImpl(`${this.baseUrl}/stats`).getRequestWithJsonResponse(
      queryParameters
    );
    return response?.data ?? response;
  }
}
